import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Toaster } from "./components/ui/sonner";

// Pages
import { LoginPage } from "./pages/LoginPage";
import StoreEntryPage from "./pages/StoreEntryPage";
import SuperAdminPage from "./pages/SuperAdminPage";
import { DashboardPage } from "./pages/DashboardPage";
import { PDVPage } from "./pages/PDVPage";
import { ProductsPage } from "./pages/ProductsPage";
import { InventoryPage } from "./pages/InventoryPage";
import { FracionamentoPage } from "./pages/FracionamentoPage";
import { CashflowPage } from "./pages/CashflowPage";
import { SalesPage } from "./pages/SalesPage";
import { CustomersPage } from "./pages/CustomersPage";
import CashCustomersPage from "./pages/CashCustomersPage";
import { SuppliersPage } from "./pages/SuppliersPage";
import { AccountsPage } from "./pages/AccountsPage";
import { PurchaseNotesPage } from "./pages/PurchaseNotesPage";
import { ReturnsPage } from "./pages/ReturnsPage";
import { UsersPage } from "./pages/UsersPage";
import { AuditPage } from "./pages/AuditPage";
import { SettingsPage } from "./pages/SettingsPage";
import { CashSessionPage } from "./pages/CashSessionPage";
import ShiftSummaryPage from "./pages/ShiftSummaryPage";
import { EmployeesPage } from "./pages/EmployeesPage";
import BillingPage from "./pages/BillingPage";
import MasterPage from "./pages/MasterPage";
import LicensePage from "./pages/LicensePage";
import { BillingProvider } from "./contexts/BillingContext";
import { BillingBlocker } from "./components/BillingBlocker";
import { LicenseProvider } from "./contexts/LicenseContext";
import { LicenseBlocker } from "./components/LicenseBlocker";
import { LicenseBanner } from "./components/LicenseBanner";
import { RequireCashSession } from "./components/RequireCashSession";
import { InstallPWA } from "./components/InstallPWA";
import { PDVErrorBoundary } from "./components/PDVErrorBoundary";
import { PDVProvider } from "./contexts/PDVContext";
import { useAuth } from "./contexts/AuthContext";
import { getHomeRoute } from "./utils/permissions";
import { hasStoreSelected } from "./utils/tenant";
import { useCompanySettings } from "./hooks/useCompanySettings";
import { useEffect } from "react";

// Aplica a identidade da empresa (favicon + título da aba) a partir das configurações salvas.
// Sem logo/nome configurados, mantém a marca padrão do Smart Flow.
function CompanyBranding() {
  const company = useCompanySettings();
  useEffect(() => {
    const name = company?.trade_name && company.trade_name !== 'Smart Flow' ? company.trade_name : 'Smart Flow';
    document.title = name;
    const logo = company?.logo_base64;
    if (logo) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
      link.type = 'image/png';
      link.href = logo;
    }
  }, [company?.logo_base64, company?.trade_name]);
  return null;
}

// Redireciona "/" para a home apropriada do usuário (admin/manager → /dashboard, demais → /pdv)
function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to={getHomeRoute(user)} replace />;
  // Sem loja escolhida ainda → telinha de entrada. Com loja escolhida → login.
  if (!hasStoreSelected()) return <Navigate to="/entrar" replace />;
  return <Navigate to="/login" replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ThemeProvider>
        <PDVProvider>
          <BrowserRouter>
          <CompanyBranding />
          <BillingProvider>
          <LicenseProvider>
          <LicenseBlocker>
          <LicenseBanner />
          <BillingBlocker>
          <Routes>
            <Route path="/entrar" element={<StoreEntryPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/master" element={<MasterPage />} />
            <Route path="/super-admin" element={<SuperAdminPage />} />
            <Route path="/billing" element={
              <ProtectedRoute>
                <BillingPage />
              </ProtectedRoute>
            } />
            <Route path="/license" element={
              <ProtectedRoute>
                <LicensePage />
              </ProtectedRoute>
            } />
            <Route path="/" element={<HomeRedirect />} />
            
            <Route path="/dashboard" element={
              <ProtectedRoute permission="dashboard.view">
                <DashboardPage />
              </ProtectedRoute>
            } />
            
            <Route path="/pdv" element={
              <ProtectedRoute>
                <PDVErrorBoundary>
                  <RequireCashSession>
                    <PDVPage />
                  </RequireCashSession>
                </PDVErrorBoundary>
              </ProtectedRoute>
            } />
            
            <Route path="/sales" element={
              <ProtectedRoute>
                <SalesPage />
              </ProtectedRoute>
            } />
            
            <Route path="/products" element={
              <ProtectedRoute>
                <ProductsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/inventory" element={
              <ProtectedRoute>
                <InventoryPage />
              </ProtectedRoute>
            } />
            
            <Route path="/fracionamento" element={
              <ProtectedRoute>
                <FracionamentoPage />
              </ProtectedRoute>
            } />
            
            <Route path="/cashflow" element={
              <ProtectedRoute>
                <CashflowPage />
              </ProtectedRoute>
            } />

            <Route path="/customers" element={
              <ProtectedRoute>
                <CustomersPage />
              </ProtectedRoute>
            } />

            <Route path="/cash-customers" element={
              <ProtectedRoute>
                <CashCustomersPage />
              </ProtectedRoute>
            } />

            <Route path="/suppliers" element={
              <ProtectedRoute>
                <SuppliersPage />
              </ProtectedRoute>
            } />

            <Route path="/accounts" element={
              <ProtectedRoute>
                <AccountsPage />
              </ProtectedRoute>
            } />

            <Route path="/purchase-notes" element={
              <ProtectedRoute>
                <PurchaseNotesPage />
              </ProtectedRoute>
            } />

            <Route path="/returns" element={
              <ProtectedRoute permission="returns.manage">
                <ReturnsPage />
              </ProtectedRoute>
            } />

            <Route path="/users" element={
              <ProtectedRoute permission="users.manage">
                <UsersPage />
              </ProtectedRoute>
            } />

            <Route path="/shift-summary" element={
              <ProtectedRoute>
                <ShiftSummaryPage />
              </ProtectedRoute>
            } />

            <Route path="/audit" element={
              <ProtectedRoute permission="audit.view">
                <AuditPage />
              </ProtectedRoute>
            } />

            <Route path="/settings" element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            } />

            <Route path="/cash-session" element={
              <ProtectedRoute>
                <CashSessionPage />
              </ProtectedRoute>
            } />

            <Route path="/employees" element={
              <ProtectedRoute permission="employees.manage">
                <EmployeesPage />
              </ProtectedRoute>
            } />
          </Routes>
          </BillingBlocker>
          </LicenseBlocker>
          </LicenseProvider>
          </BillingProvider>
        </BrowserRouter>
        <Toaster />
        <InstallPWA />
        </PDVProvider>
        </ThemeProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
